<template>
  <div>
    <FieldLabel :label="label" :tooltip="tooltip" :disabled="disabled" :required="effectiveRequired" :fieldKey="fieldKey" />

    <v-text-field
      v-if="computedMask"
      ref="textField"
      v-model="localValue"
      :class="['my-textfield', { 'my-textfield--readonly': readonly }]"
      :disabled="disabled"
      :readonly="readonly"
      :placeholder="hasPlaceholder && !readonly ? computedPlaceholder : ''"
      v-mask="computedMask"
      :type="computedType"
      :required="effectiveRequired"
      :rules="computedRules"
      :maxlength="maxlength"
      :hide-details="hideDetails ? true : 'auto'"
      color="primary"
      variant="outlined"
      density="compact"
      :autocomplete="computedAutocomplete"
      @focus="isFocused = true"
      @blur="handleBlur"
    />

    <v-text-field
      v-else
      ref="textField"
      v-model="localValue"
      :class="['my-textfield', { 'my-textfield--readonly': readonly }]"
      :disabled="disabled"
      :readonly="readonly"
      :placeholder="hasPlaceholder && !readonly ? computedPlaceholder : ''"
      :type="computedType"
      :required="effectiveRequired"
      :rules="computedRules"
      :maxlength="maxlength"
      :hide-details="hideDetails ? true : 'auto'"
      color="primary"
      variant="outlined"
      density="compact"
      :autocomplete="computedAutocomplete"
      @focus="isFocused = true"
      @blur="handleBlur"
    >
      <template v-if="type === 'password'" v-slot:append-inner>
        <component :is="computedPasswordIcon" class="textfield-icon" @click.stop="togglePasswordVisibility" />
      </template>

      <template v-if="type === 'email' && button" v-slot:append-inner>
        <Mail class="textfield-icon" @click.stop="sendEmail" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
import FieldLabel from "@/components/inputs/FieldLabel.vue";

import { Eye, EyeOff, Mail } from "lucide-vue-next";
import { mask } from "vue-the-mask";
import { clearMarker } from "@/stores/caseStore/previewMeta";
import * as validationRules from "@/utils/validationRules.js";
import sectionFieldMixin from "@/pages/pageQuestionnaire/base/sectionFieldMixin.js";

export default {
  directives: { mask },

  name: "TextField",

  mixins: [sectionFieldMixin],

  components: {
    Mail,
    FieldLabel,
  },

  inject: {
    parentKey: { default: null },
    groupId: { default: null },
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearOnDisable: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: "",
    },
    rules: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    autocomplete: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    hasPlaceholder: {
      type: Boolean,
      default: true,
    },
    button: {
      type: Boolean,
      default: false,
    },
    fieldKey: {
      type: String,
      default: "",
    },
    maxlength: {
      type: Number,
      default: 255,
    },
  },

  emits: ["update:modelValue", "blur"],

  data() {
    return {
      localValue: this.modelValue,
      isFocused: false,
      showPassword: false,
      Eye,
      EyeOff,
      Mail,
    };
  },

  watch: {
    modelValue(newValue) {
      this.localValue = newValue;
      clearMarker(this.parentKey, this.groupId, this.fieldKey, this.localValue);
      // Re-run validation so error styling (e.g. red border on required fields)
      // clears/updates when the value is set programmatically, e.g. through
      // address autofill from Google Places.
      this.$nextTick(() => {
        this.$refs.textField?.validate();
      });
    },

    localValue(newValue) {
      this.$emit("update:modelValue", newValue);
    },

    disabled(newVal) {
      if (newVal && this.clearOnDisable) {
        this.localValue = "";
        this.$emit("update:modelValue", "");
      }
      this.$refs.textField?.resetValidation();
    },
  },

  beforeUnmount() {
    this.localValue = "";
    clearMarker(this.parentKey, this.groupId, this.fieldKey, this.localValue);
    this.$emit("update:modelValue", "");
  },

  computed: {
    computedMask() {
      switch (this.type) {
        case "time":
          return "##:##";
        case "zip":
          return "#####";
        default:
          return null;
      }
    },

    computedPlaceholder() {
      if (this.placeholder) {
        return this.placeholder;
      }

      switch (this.type) {
        case "email":
          return "z.B. name@beispiel.de";
        case "url":
          return "z.B. www.beispiel.de";
        case "zip":
          return "z.B. 12345";
        case "iban":
          return "z.B. DE12 3456 7890 1234 5678 90";
        case "time":
          return "HH:MM";
        case "firstName":
          return "z.B. Max";
        case "lastName":
          return "z.B. Mustermann";
        case "fullName":
          return "z.B. Max Mustermann";
        case "streetNo":
          return this.$gt("z.B. Musterstraße 1a");
        case "city":
          return "z.B. Musterstadt";
        case "insurance":
          return "z.B. Versicherungsgesellschaft AG";
        case "insuranceNumber":
          return "z.B. VSNR 12345678";
        default:
          return "";
      }
    },

    computedRules() {
      if (this.readonly) return [];
      let finalRules = [...this.rules];

      switch (this.type) {
        case "email":
          finalRules.push(validationRules.emailRule());
          break;
        case "url":
          finalRules.push(validationRules.urlRule());
          break;
        case "zip":
          finalRules.push(validationRules.zipCodeRule());
          break;
        case "iban":
          finalRules.push(validationRules.ibanRule());
          break;
        case "time":
          finalRules.push(validationRules.timeRule());
      }

      if (this.effectiveRequired && !this.disabled) {
        finalRules.unshift(validationRules.requiredRule());
      }

      return finalRules;
    },

    computedType() {
      switch (this.type) {
        case "password":
          return this.showPassword ? "text" : this.type;
        case "email":
        case "url":
          return this.type;
        default:
          return "text";
      }
    },

    computedPasswordIcon() {
      return this.showPassword ? EyeOff : Eye;
    },

    computedAutocomplete() {
      return this.autocomplete ? "on" : "new-password";
    },
  },

  methods: {
    validate() {
      return this.$refs.textField?.validate();
    },

    focus() {
      this.$refs.textField?.focus();
    },

    handleBlur() {
      if (typeof this.localValue === "string") {
        this.localValue = this.localValue.trim();
      }

      if (this.type === "email" || this.type === "url") {
        this.localValue = this.localValue.toLowerCase();
      }

      if (this.type === "time") {
        const isValid = this.computedRules.every((rule) => rule(this.localValue) === true);
        if (!isValid) {
          this.localValue = "";
          this.$emit("update:modelValue", this.localValue);
        }
      }

      this.isFocused = false;
      this.$emit("blur");
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    sendEmail() {
      const email = this.localValue?.trim();
      const isValid = this.computedRules.every((rule) => rule(email) === true);
      if (!isValid || !email) return;

      window.open(`mailto:${email}`);
    },
  },
};
</script>

<style scoped>
.my-textfield :deep(.v-field) {
  border-radius: 8px;
}

.my-textfield :deep(input) {
  font-size: 0.9rem;
  padding-left: 12px;
  padding-right: 8px;
  border-radius: 8px;
}

:deep(.v-field__append-inner) {
  padding-left: 8px !important;
}

.my-textfield--readonly {
  /* Meeting 27.07.26: kein grauer Hintergrund mehr ("wirkt nicht bearbeitbar").
     Transparent = weiß auf den Standardseiten; Abgrenzung erfolgt über
     Border/Textfarbe des Feldes. */
  background-color: transparent;
}

.textfield-icon {
  width: 24px;
  height: 24px;
  stroke-width: 1.5;
  color: var(--grey-color);
  cursor: pointer;
}
</style>
